import { useEffect, useRef, useState } from 'react'
import { useAuthedImage } from './useAuthedImage'

/**
 * مصغَّرةُ صنفٍ في قائمة — <b>لا تُجلب حتّى تُرى</b>.
 *
 * ⚠ **والكسلُ هنا ليس تحسينًا بل شرطُ عمل.** القائمةُ تردّ مئتَي سطر، والصورةُ
 *   تُجلب بـ`fetch` لأنّ رمزَ الجلسة في ترويسةٍ لا يُرسلها المتصفّحُ مع
 *   ‏`<img src>` — فبلا كسلٍ تنطلق مئتا رحلةٍ دفعةً واحدةً عند كلِّ بحثٍ وكلِّ
 *   تبديلِ قسم، وستٌّ منها وحدَها ما يظهر على الشاشة.
 *
 * ⚠ **ولا يُلغى الجلبُ حين يخرج السطرُ من الرؤية**: المراقبُ يُفصَل عند أوّل
 *   ظهورٍ فتبقى الصورةُ محمَّلة. صورةٌ تُلغى وتُعاد مع كلّ تمريرةٍ تجعل القائمةَ
 *   ترتجف، والملفُّ في ذاكرة المتصفّح أصلًا.
 */
export function ProductThumb({
  path,
  alt,
  onClick,
}: {
  /** مسارُ الصورة، أو `null` لصنفٍ بلا صورة. */
  path: string | null
  alt: string
  /** يُنادى بالضغط. غيابُه يجعلها صورةً تُرى لا زرًّا يُضغط. */
  onClick?: () => void
}) {
  const box = useRef<HTMLDivElement>(null)

  // ⚠ وبلا `IntersectionObserver` يبدأ «مرئيًّا» فتُحمَّل الكلُّ: المتصفّحاتُ
  //   التي تخدم الشاشةَ كلُّها تعرفه، ومن لا يعرفه أولى به أن يحمّل من ألّا
  //   يرى. ويُقرَّر عند التهيئة لا في أثرٍ يضبط الحالةَ متزامنًا — وذاك يبدأ
  //   رسمةً ثانيةً لكلّ سطرٍ من مئتين.
  const [seen, setSeen] = useState(() => typeof IntersectionObserver !== 'function')

  useEffect(() => {
    const element = box.current

    if (element === null || seen) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true)
          observer.disconnect()
        }
      },

      // هامشٌ فوق الطيّة: الصورةُ تبدأ قبل أن يصل السطرُ إلى العين.
      { rootMargin: '300px' },
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [seen])

  const image = useAuthedImage(seen ? path : null)

  const face = (
    <div
      ref={box}
      className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded border border-line bg-surface"
    >
      {path === null
        ? <span className="text-[10px] leading-tight text-ink-soft">لا صورة</span>
        : image.url === null
          ? <span className="text-[10px] text-ink-soft">…</span>
          : <img src={image.url} alt={alt} className="h-full w-full object-contain" />}
    </div>
  )

  if (onClick === undefined) {
    return face
  }

  return (
    <button
      type="button"
      onClick={onClick}
      // ⚠ والوصفُ يُقال بالكلمات: الزرُّ كلُّه صورةٌ أو فراغٌ، ومن يقرأ الشاشةَ
      //   بالصوت لا يسمع منه شيئًا بلا هذا.
      aria-label={path === null ? `أضف صورةً لـ${alt}` : `بدّل صورةَ ${alt}`}
      title={path === null ? 'أضف صورة' : 'بدّل الصورة'}
      className="shrink-0 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {face}
    </button>
  )
}
